import type { ListLogsQuery, ListLogsResponse } from "@woven/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ControlApiError } from "../../lib/api";
import { useControlApiClient } from "../../lib/hooks";
import { startSequentialPolling } from "../../lib/polling";

const LOG_POLL_INTERVAL_MS = 10_000;

export type LogsState =
  | { status: "loading" }
  | { status: "ready"; data: ListLogsResponse }
  | { status: "error"; message: string };

export function useLogs(
  query: ListLogsQuery,
  live: boolean
): { state: LogsState; reload: () => void } {
  const client = useControlApiClient();
  const queryKey = JSON.stringify(query);
  const [revision, setRevision] = useState(0);
  const scope = useMemo(
    () => ({ client, query: JSON.parse(queryKey) as ListLogsQuery, live, revision }),
    [client, queryKey, live, revision]
  );
  const [snapshot, setSnapshot] = useState<{ scope: typeof scope; state: LogsState } | null>(null);
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(
    () =>
      startSequentialPolling({
        intervalMs: LOG_POLL_INTERVAL_MS,
        enabled: scope.live && !scope.query.cursor,
        load: (signal) => scope.client.listLogs(scope.query, signal),
        onSuccess: (data) => setSnapshot({ scope, state: { status: "ready", data } }),
        onError: (error) =>
          setSnapshot({
            scope,
            state: {
              status: "error",
              message:
                error instanceof ControlApiError
                  ? error.message
                  : "Unable to load logs. Please try again.",
            },
          }),
      }),
    [scope]
  );

  return { state: snapshot?.scope === scope ? snapshot.state : { status: "loading" }, reload };
}
